import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Grid, Paper, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { mastersApi } from "../../../../services/MasterDataServices";
import PageWrapper from "../../../../common/PageWrapper/PageWrapper";

function toTitleCase(value) {
  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function tryFormatBhutanDateTime(value) {
  if (typeof value !== "string") return null;
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Thimphu",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(d);
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  const formattedDate = tryFormatBhutanDateTime(value);
  if (formattedDate) return formattedDate;

  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    const looksLikeDimensionItems = value.every((x) => x && typeof x === "object" && "label" in x && "unit" in x);
    if (looksLikeDimensionItems) {
      return value.map((x) => `${x.label ?? "-"} - ${x.unit ?? "-"}`).join("\n");
    }
    return JSON.stringify(value, null, 2);
  }

  if (typeof value === "object") {
    if ("name" in value && "code" in value) return `${value.name} (${value.code})`;
    if ("name" in value) return String(value.name);
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

export default function MasterDataDetailsPage() {
  const navigate = useNavigate();
  const { resource, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const resourceLabel = useMemo(() => toTitleCase(resource || ""), [resource]);

  useEffect(() => {
    if (!resource || !id) return;
    setLoading(true);
    mastersApi(resource, "get", null, null, id)
      .then((res) => setData(res?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [resource, id]);

  const rows = useMemo(() => {
    if (!data) return [];
    const hiddenKeys = new Set(["id"]);
    if (resource === "master_items") {
      hiddenKeys.add("category");
      hiddenKeys.add("category_id");
      hiddenKeys.add("category_name");
    }

    const keys = Object.keys(data).filter((k) => !hiddenKeys.has(k));

    const ordered = [];
    ["code", "name", "description"].forEach((k) => {
      if (keys.includes(k)) ordered.push(k);
    });

    if (keys.includes("is_deleted")) ordered.push("is_deleted");

    keys
      .filter((k) => !ordered.includes(k))
      .sort()
      .forEach((k) => ordered.push(k));

    return ordered.map((k) => {
      if (k === "is_deleted") {
        return { key: "status", label: "Status", value: data?.is_deleted ? "Inactive" : "Active" };
      }
      return { key: k, label: toTitleCase(k), value: formatValue(data[k]) };
    });
  }, [data]);

  return (
    <PageWrapper>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">{resourceLabel || "Master"} Detail</Typography>
          <Button variant="outlined" onClick={() => navigate(-1)} className={"text-capitalize"}>
            Back
          </Button>
        </Box>

        <Paper className={"box-shadow"} sx={{ p: 2 }}>
          <Typography sx={{ mb: 2, color: "#616B88" }}>{resourceLabel}</Typography>

          {loading ? (
            <Typography>Loading...</Typography>
          ) : !data ? (
            <Typography color={"error"}>Not found</Typography>
          ) : (
            <Grid container spacing={2}>
              {rows.map((r) => (
                <React.Fragment key={r.key}>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ color: "#B3B3B3" }}>{r.label}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography
                      sx={{ color: "#3D3D3D", wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                      component={typeof r.value === "string" && r.value.includes("\n") ? "pre" : "div"}
                    >
                      {r.value}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          )}
        </Paper>
      </Box>
    </PageWrapper>
  );
}
